// src/Components/CustomerOrderUploadFolder/CustomerOrderUploadFolder.js
import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  Alert,
  Button,
  Form,
  Modal,
  ProgressBar,
  Spinner,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolderPlus } from '@fortawesome/free-solid-svg-icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './CustomerOrderUploadFolder.css';

/* ================= Config ================= */
function ensureHttpBase(u) {
  let s = String(u || '').trim();
  if (!/^https?:\/\//i.test(s)) s = `http://${s}`;
  return s.replace(/\/+$/, '');
}

const API_BASE = ensureHttpBase(
  process.env.REACT_APP_API_BASE || 'https://databank.tcscontrols.com.my'
);
const CHUNK_SIZE_BYTES = 8 * 1024 * 1024;

/** Hard root: cannot upload above this path */
const CO_ROOT =
  process.env.REACT_APP_CUSTOMER_ORDER_START_PATH || '/customer-order';

/* ================= Helpers ================= */
const getRelPath = (file) =>
  (file.webkitRelativePath || file.name).replace(/\\/g, '/');

const normalizePath = (p) =>
  (
    '/' +
    String(p || '')
      .trim()
      .replace(/\\/g, '/')
      .replace(/^\/+/, '')
  ).replace(/\/{2,}/g, '/');

const clampToCustomerOrder = (dest) => {
  const root = normalizePath(CO_ROOT);
  const wanted = normalizePath(dest || root);
  if (wanted === root) return root;
  return wanted.startsWith(root + '/') ? wanted : root;
};

const fmtBytes = (n) => {
  if (!Number.isFinite(n)) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 && i ? 2 : v < 100 && i ? 1 : 0)} ${units[i]}`;
};

const makeUploadSessionId = () =>
  `co_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;

function collectDirectories(relativePaths) {
  const dirs = new Set();

  for (const relPath of relativePaths) {
    const parts = String(relPath || '')
      .replace(/\\/g, '/')
      .split('/')
      .filter(Boolean);

    let accum = '';
    for (let i = 0; i < parts.length - 1; i++) {
      accum = accum ? `${accum}/${parts[i]}` : parts[i];
      dirs.add(accum);
    }
  }

  return Array.from(dirs).sort((a, b) => a.localeCompare(b));
}

// Build a directory tree (for summary rendering)
function buildTree(relativePaths) {
  const root = { name: '', type: 'dir', children: new Map() };
  for (const p of relativePaths) {
    const parts = p.split('/').filter(Boolean);
    let node = root;
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      const isFile = i === parts.length - 1;
      const key = part + (isFile ? '::file' : '::dir');
      if (!node.children.has(key)) {
        node.children.set(key, {
          name: part,
          type: isFile ? 'file' : 'dir',
          children: isFile ? null : new Map(),
        });
      }
      node = node.children.get(key);
    }
  }
  return root;
}

function TreeView({ node, depth = 0 }) {
  const entries = node.children ? Array.from(node.children.values()) : [];
  entries.sort((a, b) => {
    if (a.type !== b.type) return a.type === 'dir' ? -1 : 1;
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  });

  return (
    <div style={{ marginLeft: depth ? 14 : 0 }}>
      {entries.map((child, idx) =>
        child.type === 'dir' ? (
          <div key={`d-${depth}-${idx}-${child.name}`}>
            <div className="text-muted small">Folder: {child.name}</div>
            <TreeView node={child} depth={depth + 1} />
          </div>
        ) : (
          <div key={`f-${depth}-${idx}-${child.name}`} className="small">
            - {child.name}
          </div>
        )
      )}
    </div>
  );
}

TreeView.propTypes = {
  node: PropTypes.shape({
    name: PropTypes.string,
    type: PropTypes.oneOf(['dir', 'file']),
    children: PropTypes.instanceOf(Map),
  }),
  depth: PropTypes.number,
};

/* ================= Component ================= */
function CustomerOrderUploadFolder({ currentPath, userId, onFolderUploaded }) {
  const [showModal, setShowModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [progressPct, setProgressPct] = useState(0);
  const [bytesLoaded, setBytesLoaded] = useState(0);
  const [expectedBytes, setExpectedBytes] = useState(0);

  const [rootFolderName, setRootFolderName] = useState('');
  const [selectedMeta, setSelectedMeta] = useState(null);
  const [uploadedSummary, setUploadedSummary] = useState(null);
  const [missing, setMissing] = useState([]);

  const inputRef = useRef(null);
  const abortRef = useRef(null);

  const destBase = clampToCustomerOrder(currentPath || CO_ROOT);
  const displayTarget = rootFolderName
    ? `${destBase.replace(/\/+$/, '')}/${rootFolderName}`
    : destBase;

  const resetState = () => {
    setProgressPct(0);
    setBytesLoaded(0);
    setExpectedBytes(0);
    setIsUploading(false);
    setRootFolderName('');
    setSelectedMeta(null);
    setUploadedSummary(null);
    setMissing([]);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleOpen = () => {
    resetState();
    setShowModal(true);
  };

  const handleFolderPicked = () => {
    const fileList = inputRef.current?.files;
    if (!fileList || fileList.length === 0) {
      setRootFolderName('');
      setSelectedMeta(null);
      setExpectedBytes(0);
      return;
    }

    const files = Array.from(fileList);
    const first = files.find((f) => f.webkitRelativePath) || files[0];
    if (first && first.webkitRelativePath) {
      setRootFolderName(first.webkitRelativePath.split('/')[0] || '');
    } else {
      setRootFolderName('');
    }

    const relPaths = files.map(getRelPath);
    const totalBytes = files.reduce((sum, f) => sum + (f.size || 0), 0);

    setSelectedMeta({
      count: files.length,
      totalBytes,
      relPaths,
    });
    setExpectedBytes(totalBytes);
  };

  const handleCancelUpload = () => {
    try {
      abortRef.current?.abort();
      toast.info('Upload canceled.');
    } catch {
      // noop
    }
    setIsUploading(false);
    abortRef.current = null;
  };

  const handleFolderSubmit = async (e) => {
    e.preventDefault();

    const fileList = inputRef.current?.files;
    if (!fileList || fileList.length === 0) {
      toast.error('Please select a folder to upload.');
      return;
    }

    const files = Array.from(fileList);
    const relPaths = files.map(getRelPath);
    const totalBytes = files.reduce((sum, f) => sum + (f.size || 0), 0);
    const relativeDirs = collectDirectories(relPaths);

    setExpectedBytes(totalBytes);
    setBytesLoaded(0);
    setProgressPct(0);
    setMissing([]);
    setUploadedSummary(null);
    setIsUploading(true);

    const controller = new AbortController();
    abortRef.current = controller;

    let completedBytes = 0;
    const uploadedAbsAll = [];

    try {
      for (const relDir of relativeDirs) {
        const fullDir = `${destBase.replace(/\/+$/, '')}/${relDir}`.replace(
          /\/{2,}/g,
          '/'
        );
        const res = await axios.post(
          `${API_BASE}/api/folder`,
          { path: fullDir },
          {
            signal: controller.signal,
            timeout: 0,
            withCredentials: true,
          }
        );

        if (!res?.data?.ok) {
          throw new Error(res?.data?.error || `Failed to create folder: ${fullDir}`);
        }
      }

      for (const file of files) {
        const relPath = getRelPath(file);
        const parentRel = relPath.includes('/')
          ? relPath.slice(0, relPath.lastIndexOf('/'))
          : '';
        const targetDir = parentRel
          ? `${destBase.replace(/\/+$/, '')}/${parentRel}`.replace(/\/{2,}/g, '/')
          : destBase;

        const fileBytes = Number(file.size) || 0;
        const totalChunks = Math.max(1, Math.ceil(fileBytes / CHUNK_SIZE_BYTES));
        const sessionId = makeUploadSessionId();
        let uploadedBytesForFile = 0;

        for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
          const start = chunkIndex * CHUNK_SIZE_BYTES;
          const end = Math.min(fileBytes, start + CHUNK_SIZE_BYTES);
          const chunkBlob = file.slice(start, end);

          const fd = new FormData();
          fd.append('chunk', chunkBlob, file.name);
          fd.append('sessionId', sessionId);
          fd.append('path', targetDir);
          fd.append('filename', file.name);
          fd.append('chunkIndex', String(chunkIndex));
          fd.append('totalChunks', String(totalChunks));

          const chunkRes = await axios.post(`${API_BASE}/api/upload-chunk`, fd, {
            signal: controller.signal,
            onUploadProgress: (evt) => {
              const loaded = Math.max(0, evt.loaded ?? 0);
              const aggregateLoaded = Math.min(
                totalBytes,
                completedBytes + uploadedBytesForFile + loaded
              );
              const denom = Math.max(totalBytes, 1);

              setBytesLoaded(aggregateLoaded);
              setProgressPct(Math.round((aggregateLoaded / denom) * 100));
            },
            maxContentLength: Infinity,
            maxBodyLength: Infinity,
            timeout: 0,
            withCredentials: true,
          });

          if (!chunkRes?.data?.ok) {
            throw new Error(
              chunkRes?.data?.error || `Chunk upload failed for ${relPath}`
            );
          }

          uploadedBytesForFile += chunkBlob.size;
          setBytesLoaded(
            Math.min(totalBytes, completedBytes + uploadedBytesForFile)
          );
          setProgressPct(
            Math.round(
              (Math.min(totalBytes, completedBytes + uploadedBytesForFile) /
                Math.max(totalBytes, 1)) *
                100
            )
          );
        }

        const res = await axios.post(
          `${API_BASE}/api/upload-complete`,
          {
            sessionId,
            path: targetDir,
            filename: file.name,
            totalChunks,
          },
          {
            signal: controller.signal,
            timeout: 0,
            withCredentials: true,
          }
        );

        if (!res?.data?.ok) {
          throw new Error(res?.data?.error || `Upload failed for ${relPath}`);
        }

        completedBytes = Math.min(totalBytes, completedBytes + fileBytes);
        setBytesLoaded(completedBytes);
        setProgressPct(
          Math.round((completedBytes / Math.max(totalBytes, 1)) * 100)
        );

        const uploadedPath = String(res.data?.uploaded?.to || '').trim();
        if (uploadedPath) uploadedAbsAll.push(uploadedPath);
      }

      setBytesLoaded(totalBytes);
      setProgressPct(100);

      const base = destBase.replace(/\/+$/, '');
      const uploadedRel = uploadedAbsAll
        .map((abs) =>
          abs.startsWith(base + '/') ? abs.slice(base.length + 1) : abs
        )
        .sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' })
        );

      setUploadedSummary({ base, relPaths: uploadedRel });

      const expectedSet = new Set(relPaths.map((p) => p.replace(/^\/+/, '')));
      const gotSet = new Set(uploadedRel.map((p) => p.replace(/^\/+/, '')));
      const missingList = [];

      for (const p of expectedSet) {
        if (!gotSet.has(p)) missingList.push(p);
      }

      setMissing(missingList);

      onFolderUploaded?.({
        uid: userId,
        name: rootFolderName || 'Folder Upload',
        count: uploadedAbsAll.length,
        base,
        uploaded: uploadedAbsAll,
        currentPath: destBase,
      });

      toast.success(`Uploaded ${uploadedAbsAll.length} file(s) to ${displayTarget}`);
      if (missingList.length === 0) {
        toast.success('All files uploaded successfully.');
      } else {
        toast.error('Some files were not confirmed on the server.');
      }
    } catch (err) {
      if (err?.name === 'CanceledError' || axios.isCancel?.(err)) {
        toast.info('Upload canceled.');
      } else {
        const msg =
          err?.response?.data?.error ||
          err?.message ||
          'Upload failed. Please try again.';
        toast.error(msg);
      }
    } finally {
      setIsUploading(false);
      abortRef.current = null;
    }
  };

  const tree = useMemo(() => {
    if (!uploadedSummary) return null;
    return buildTree(uploadedSummary.relPaths);
  }, [uploadedSummary]);

  const selectedCount = selectedMeta?.count || 0;
  const modalTitle = isUploading
    ? 'Uploading...'
    : uploadedSummary
    ? 'Uploaded'
    : 'Upload Folder';

  return (
    <>
      <Button
        onClick={handleOpen}
        variant="outline-dark"
        className="d-flex align-items-center rounded-2"
      >
        <FontAwesomeIcon icon={faFolderPlus} />
        &nbsp; Upload Folder
      </Button>

      <Modal
        show={showModal}
        onHide={() => (!isUploading ? setShowModal(false) : null)}
        centered
        size="lg"
        backdrop={isUploading ? 'static' : true}
        keyboard={!isUploading}
      >
        <Modal.Header closeButton={!isUploading}>
          <Modal.Title>{modalTitle}</Modal.Title>
          {isUploading && (
            <div className="ms-auto">
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleCancelUpload}
              >
                Cancel upload
              </Button>
            </div>
          )}
        </Modal.Header>

        <Modal.Body>
          {isUploading && (
            <div className="d-flex align-items-center gap-2 mb-2">
              <Spinner animation="border" size="sm" />
              <span className="small text-muted">
                Please keep this window open until upload completes...
              </span>
            </div>
          )}

          {(isUploading || progressPct > 0) && expectedBytes > 0 && (
            <>
              <ProgressBar now={progressPct} label={`${progressPct}%`} />
              <div className="d-flex justify-content-between small mt-1">
                <span>
                  Sent:&nbsp;<strong>{fmtBytes(bytesLoaded)}</strong>
                </span>
                <span>
                  Folder size:&nbsp;<strong>{fmtBytes(expectedBytes)}</strong>
                </span>
              </div>
              {selectedMeta && (
                <div className="small text-muted">
                  Files selected: <strong>{selectedCount}</strong>
                </div>
              )}
            </>
          )}

          {uploadedSummary ? (
            <>
              {missing.length === 0 ? (
                <Alert variant="success" className="py-2">
                  All files confirmed on server. Nothing missing.
                </Alert>
              ) : (
                <Alert variant="danger" className="py-2">
                  Some files are missing on the server ({missing.length}).
                </Alert>
              )}

              <div className="mb-2 small">
                Uploaded to:&nbsp;<code>{uploadedSummary.base}</code>
              </div>

              <div
                className="border rounded p-2"
                style={{ maxHeight: 320, overflow: 'auto' }}
              >
                {tree ? (
                  <TreeView node={tree} />
                ) : (
                  <div className="small text-muted">No files?</div>
                )}
              </div>

              <div className="mt-2 small text-muted">
                Server-confirmed files:&nbsp;
                <strong>{uploadedSummary.relPaths.length}</strong>
                {selectedCount ? (
                  <>
                    &nbsp;| Selected:&nbsp;<strong>{selectedCount}</strong>
                  </>
                ) : null}
              </div>

              {missing.length > 0 && (
                <details className="mt-2">
                  <summary className="small text-danger">
                    Show missing files
                  </summary>
                  <div
                    className="border rounded p-2 mt-2"
                    style={{ maxHeight: 200, overflow: 'auto' }}
                  >
                    {missing.map((m) => (
                      <div key={m} className="small text-danger">
                        x {m}
                      </div>
                    ))}
                  </div>
                </details>
              )}

              <div className="d-flex gap-2 justify-content-end mt-3">
                <Button variant="secondary" onClick={() => setShowModal(false)}>
                  Close
                </Button>
              </div>
            </>
          ) : (
            <Form onSubmit={handleFolderSubmit} encType="multipart/form-data">
              <div className="small text-muted mb-2">
                Destination:&nbsp;<code>{destBase}</code>
              </div>

              <Form.Group controlId="formFolder" className="my-2">
                <Form.Label className="fw-semibold">Choose a folder</Form.Label>
                <Form.Control
                  ref={inputRef}
                  type="file"
                  webkitdirectory="true"
                  directory="true"
                  multiple
                  onChange={handleFolderPicked}
                  disabled={isUploading}
                />
                <div className="mt-2 small text-muted">
                  Target:&nbsp;<code>{displayTarget}</code>
                </div>

                {selectedMeta && (
                  <div className="mt-2 small">
                    Selected files:&nbsp;<strong>{selectedCount}</strong>
                    &nbsp;• Folder size:&nbsp;
                    <strong>{fmtBytes(selectedMeta.totalBytes)}</strong>
                  </div>
                )}
              </Form.Group>

              <Button
                type="submit"
                className="w-100 mt-3"
                variant="primary"
                disabled={isUploading}
              >
                {isUploading ? 'Uploading...' : 'Upload Folder'}
              </Button>
            </Form>
          )}
        </Modal.Body>
      </Modal>

      <ToastContainer position="top-right" autoClose={2000} />
    </>
  );
}

CustomerOrderUploadFolder.propTypes = {
  currentPath: PropTypes.string,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onFolderUploaded: PropTypes.func,
};

export default CustomerOrderUploadFolder;
